import React, { useEffect, useState } from 'react';
import type { Map as MapboxMap, GeoJSONSource } from 'mapbox-gl';
import type { MeinersFootprintData } from '../../lib/api';
import { useScrollSync } from './ScrollSyncProvider';

const SOURCE = 'meiners-footprint';
const LAYERS = ['meiners-screen-fill', 'meiners-screen-line', 'meiners-candidate-label'];
const EMPTY: GeoJSON.FeatureCollection = { type: 'FeatureCollection', features: [] };
const colors = { remaining: '#70bca8', flood: '#82a9d2', buffer: '#e88468', row: '#e4b97a', candidate: '#f9efd3' };

export function MeinersFootprintControls({ map, layerRevision, footprint }: { map: MapboxMap | null; layerRevision: object; footprint: MeinersFootprintData }) {
  const { footprintScreen, setFootprintScreen } = useScrollSync();
  const [ready, setReady] = useState(false);
  const [candidate, setCandidate] = useState('A');
  const scenario = footprint.scenarios.find(s => s.pad_buffer_ft === (footprintScreen || 500))!;
  const selected = footprint.candidates.features.find(f => f.properties.label === candidate)!;
  const retained = footprintScreen === 1000 ? selected.properties.usable_at_1000_acres : selected.properties.acres;
  const conflict = footprintScreen === 1000 && !selected.properties.is_fully_clear_at_1000ft;

  useEffect(() => {
    if (!map) return;
    let cancelled = false;
    const setup = () => {
      if (cancelled) return;
      if (!map.isStyleLoaded()) { map.once('idle', setup); return; }
      if (!map.getSource(SOURCE)) map.addSource(SOURCE, { type: 'geojson', data: EMPTY });
      if (!map.getLayer(LAYERS[0])) map.addLayer({
        id: LAYERS[0], type: 'fill', source: SOURCE,
        paint: { 'fill-color': ['get', 'color'], 'fill-opacity': ['case', ['==', ['get', 'kind'], 'candidate'], 0.18, 0.3] },
      });
      if (!map.getLayer(LAYERS[1])) map.addLayer({
        id: LAYERS[1], type: 'line', source: SOURCE,
        paint: { 'line-color': ['get', 'color'], 'line-width': ['case', ['==', ['get', 'kind'], 'candidate'], 3, 1], 'line-opacity': 0.95 },
      });
      if (!map.getLayer(LAYERS[2])) map.addLayer({
        id: LAYERS[2], type: 'symbol', source: SOURCE, filter: ['==', ['get', 'kind'], 'candidate'],
        layout: { 'text-field': ['get', 'label'], 'text-size': 11, 'text-max-width': 22, 'text-allow-overlap': true },
        paint: { 'text-color': '#fff8e7', 'text-halo-color': '#253540', 'text-halo-width': 2 },
      });
      setReady(true);
    };
    const frame = requestAnimationFrame(setup);
    return () => {
      cancelled = true;
      map.off('idle', setup);
      cancelAnimationFrame(frame);
      setReady(false);
      try {
        for (const layer of [...LAYERS].reverse()) if (map.getLayer(layer)) map.removeLayer(layer);
        if (map.getSource(SOURCE)) map.removeSource(SOURCE);
      } catch { /* The enclosing map may already have been removed. */ }
    };
  }, [map, layerRevision]);

  useEffect(() => {
    if (!ready || !map) return;
    const source = map.getSource(SOURCE) as GeoJSONSource | undefined;
    if (!source) return;
    if (!footprintScreen) { source.setData(EMPTY); return; }
    const features: GeoJSON.Feature[] = [];
    const add = (collection: { features: unknown[] }, kind: keyof typeof colors) => {
      for (const raw of collection.features) {
        const f = raw as GeoJSON.Feature;
        features.push({ ...f, properties: { ...f.properties, kind, color: colors[kind] } });
      }
    };
    add(scenario.geometry, 'remaining');
    add(footprint.flood, 'flood');
    add(scenario.exclusions.gathering_row, 'row');
    add(scenario.exclusions.active_pad_buffers, 'buffer');
    features.push({ ...selected, properties: { ...selected.properties, kind: 'candidate', color: conflict ? '#ff9877' : colors.candidate, label: conflict ? `${candidate} · ${retained.toFixed(1)} ac screen-clear\n${Math.round(selected.properties.acres)} ac original outline` : `${candidate} · ${Math.round(selected.properties.acres)} ac` } } as GeoJSON.Feature);
    source.setData({ type: 'FeatureCollection', features });
    // Keep wellheads legible above the screen polygons.
    for (const id of ['briefing-oil-gas-wells', LAYERS[2]]) if (map.getLayer(id)) map.moveLayer(id);
  }, [map, ready, footprintScreen, footprint, scenario, selected, candidate, conflict, retained]);

  useEffect(() => {
    if (!map || !ready || !footprintScreen) return;
    const points: number[][] = [];
    const walk = (value: unknown): void => {
      if (!Array.isArray(value)) return;
      if (typeof value[0] === 'number') points.push(value as number[]);
      else value.forEach(walk);
    };
    footprint.boundary.features.forEach(f => walk(f.geometry.coordinates));
    const height = map.getContainer().clientHeight;
    map.fitBounds([
      [Math.min(...points.map(p => p[0])), Math.min(...points.map(p => p[1]))],
      [Math.max(...points.map(p => p[0])), Math.max(...points.map(p => p[1]))],
    ], { padding: { top: Math.min(280, Math.round(height * 0.4)), bottom: Math.min(55, Math.round(height * 0.1)), left: 25, right: 25 }, duration: 600, maxZoom: 13 });
  }, [map, ready, footprintScreen, footprint]);

  const button = (active: boolean): React.CSSProperties => ({
    color: '#f4f1e8', background: active ? '#536673' : 'transparent', border: `1px solid ${active ? '#d8c3a1' : '#6b7a83'}`,
    padding: '4px 8px', borderRadius: 3, cursor: 'pointer', fontSize: 11,
  });
  return <div className="briefing-controls meiners-footprint-controls" style={{ background: 'rgba(39,52,61,.95)', color: '#f4f1e8', padding: '8px 10px', borderRadius: 4, maxWidth: 330, fontSize: 11, lineHeight: 1.5 }}>
    <style>{`@media (max-width: 767px) { .meiners-footprint-controls { max-height: 145px; overflow-y: auto; } }`}</style>
    <button type="button" aria-expanded={!!footprintScreen} onClick={() => setFootprintScreen(footprintScreen ? null : 500)} style={{ color: 'inherit', background: 'none', border: 0, padding: 0, cursor: 'pointer', fontWeight: 600, fontSize: 12 }}>
      {footprintScreen ? '−' : '+'} Development footprint
    </button>
    {footprintScreen && <>
      <div style={{ display: 'flex', gap: 6, alignItems: 'center', marginTop: 7 }}>
        <span>Well buffers</span>
        {([500, 1000] as const).map(value => <button key={value} type="button" aria-pressed={footprintScreen === value} onClick={() => setFootprintScreen(value)} style={button(footprintScreen === value)}>{value.toLocaleString()} ft</button>)}
      </div>
      <div style={{ marginTop: 6 }}><strong>{Math.round(scenario.residual_acres).toLocaleString()} ac</strong> remain · largest piece <strong>{Math.round(scenario.largest_contiguous_acres)} ac</strong></div>
      <div style={{ display: 'flex', gap: 5, alignItems: 'center', marginTop: 7 }}>
        <span>Inspect</span>
        {footprint.candidates.features.map(f => <button key={f.properties.label} type="button" aria-pressed={candidate === f.properties.label} onClick={() => setCandidate(f.properties.label)} style={button(candidate === f.properties.label)}>{f.properties.label} · {Math.round(f.properties.acres)} ac</button>)}
      </div>
      <div style={{ marginTop: 6, color: conflict ? '#ffb49b' : '#d7e9df' }}>
        {conflict ? `${retained.toFixed(1)} ac remain; the original rectangle conflicts with the larger buffer.` : 'Selected rectangle clears this desktop screen.'}
      </div>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '2px 10px', marginTop: 7 }}>
        {(['remaining', 'buffer', 'row', 'flood'] as const).map(key => <span key={key}><i style={{ display: 'inline-block', width: 8, height: 8, marginRight: 4, background: colors[key] }} />{({remaining:'Remaining',buffer:'Well buffer',row:'Gathering',flood:'Flood'})[key]}</span>)}
      </div>
      <div style={{ color: '#ced5d8', fontSize: 10, marginTop: 7 }}>Illustrative buffers, not required setbacks. Gathering corridor: 25 ft each side. Alternatives overlap; do not add their acres. Wetlands, roads, facilities and surface rights still require review.</div>
    </>}
  </div>;
}
